import { Controller, useForm } from "react-hook-form";
import { AppBox, AppButton, AppInput, AppText } from "../../../../../commons/components";
import { findInputError, isFormInvalid } from "../../../../../commons/utilities/form-utils";
import { IFootballSeason } from "../../../../../commons/models/football/interface/i-football-season";
import { useAppDispatch, useAppSelector } from "../../../../../hooks/app";
import { useSnackbar } from "../../../../../redux/snackbarProvider";
import { AxiosError } from "axios";
import { IFootballSeasonGroup } from "../../../../../commons/models/football/admin/interface/i-manage-football-season-group";
import { IGroupInfo } from "../../../../../api-services/football/admin/manage-football-season-service";
import { fetchSeasonTeamGroupList } from "../../../../../redux/slices/football/admin/season/team-group/manage-team-group-list-slice";
import { IServerErrorResponse } from "../../../../../commons/components/interface";
import { seasonTeamGroupEdit } from "../../../../../redux/slices/football/admin/season/team-group/manage-team-group-edit-slice";
import { seasonTeamGroupAdd } from "../../../../../redux/slices/football/admin/season/team-group/manage-team-group-add-slice";
import { StatusEnum } from "../../../../../commons/enums/status-enum";
import { toSentenceCase } from "../../../../../commons/utilities/string-utils";
import { IFootballHostedCompetition } from "../../../../../commons/models/football/interface/i-football-competition";

interface SeasonTeamGroupAddEditProps {
  teamGroupData?: IFootballSeasonGroup | null;
  onSave?: () => void;
  onClose?: () => void;
}

export default function SeasonTeamGroupAddEdit({ teamGroupData, onSave, onClose }: SeasonTeamGroupAddEditProps) {
  const dispatch = useAppDispatch();
  const addSnackbar = useSnackbar();
  const methods = useForm();
  const formErrors = methods.formState.errors;
  const { fetchedCompetition } = useAppSelector((state) => state.fetchedCompetition);
  const { seasonTeamGroupAddResponseStatus } = useAppSelector((state) => state.footballManageSeasonTeamGroupAdd);
  const { seasonTeamGroupEditResponseStatus } = useAppSelector((state) => state.footballManageSeasonTeamGroupEdit);
  const handleFormSubmit = (values: IGroupInfo) => {
    dispatch(
      teamGroupData
        ? seasonTeamGroupEdit({
            seasonId: fetchedCompetition.latestSeason?.id as number,
            teamGroupId: teamGroupData.id,
            seasonTeamGroup: values,
            urlParams: {
              hostTeamId: (fetchedCompetition as IFootballHostedCompetition).hostTeam?.id,
            },
          })
        : seasonTeamGroupAdd({
            seasonId: fetchedCompetition.latestSeason?.id as number,
            seasonTeamGroup: values,
            urlParams: {
              hostTeamId: (fetchedCompetition as IFootballHostedCompetition).hostTeam?.id,
            },
          })
    )
      .unwrap()
      .then(() => {
        dispatch(
          fetchSeasonTeamGroupList({
            seasonId: fetchedCompetition.latestSeason?.id as number,
            urlParams: {
              hostTeamId: (fetchedCompetition as IFootballHostedCompetition).hostTeam?.id,
            },
          })
        );
        if (teamGroupData) {
          addSnackbar({
            key: "team-group-edit-success",
            text: "Team Group Edited Successfully",
            variant: "success",
          });
        } else {
          addSnackbar({
            key: "team-group-add-success",
            text: "Team Group Added Successfully",
            variant: "success",
          });
        }
        if (onSave) {
          onSave();
        }
      })
      .catch((error: AxiosError<IServerErrorResponse>) => {
        const responseData = error.response?.data;
        if (error.response?.status === 417) {
          if (responseData) {
            addSnackbar({
              key: "error",
              text: "Form not valid",
              variant: "danger",
            });
            Object.entries(responseData).forEach(([field, messages]) => {
              messages.forEach((message: string) => {
                methods.setError(field, { message });
              });
            });
          }
        } else {
          addSnackbar({
            key: "error",
            text: responseData?.message,
            variant: "danger",
          });
        }
      });
  };

  return (
    <form
      noValidate
      onSubmit={methods.handleSubmit((e) => {
        methods.formState.isValid && handleFormSubmit(e as IFootballSeasonGroup);
      })}
    >
      <AppBox flexDirection="column" gap="md">
        <AppBox flexDirection="column" gap="sm">
          <Controller
            name="name"
            defaultValue={teamGroupData ? teamGroupData.name : ""}
            control={methods.control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <AppBox flexDirection="column">
                <AppInput
                  id="name"
                  label="Group Name"
                  placeholder="Enter a group name, e.g. Group A"
                  type="text"
                  defaultValue={value}
                  onChange={onChange}
                />
                {isFormInvalid(findInputError(formErrors, "name")) && (
                  <AppText as="span" color="danger">
                    <>{toSentenceCase(`${formErrors.name?.message ?? "Group name is required."}`)}</>
                  </AppText>
                )}
              </AppBox>
            )}
          />
        </AppBox>
        <AppBox justifyContent="end" gap="xs">
          <AppButton
            variant="outline"
            borderLight
            label="Cancel"
            color="gray"
            onClick={(e) => {
              e.preventDefault();
              onClose?.();
            }}
            disabled={
              seasonTeamGroupEditResponseStatus === StatusEnum.Loading ||
              seasonTeamGroupAddResponseStatus === StatusEnum.Loading
            }
          />
          <AppButton
            type="submit"
            disabled={!methods.formState.isValid}
            label={teamGroupData ? "Save Changes" : "Add Group"}
            loading={
              seasonTeamGroupEditResponseStatus === StatusEnum.Loading ||
              seasonTeamGroupAddResponseStatus === StatusEnum.Loading
            }
          />
        </AppBox>
      </AppBox>
    </form>
  );
}
